// assets/AllCourses/Exercises/WordJumblesExercise.jsx

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function WordJumblesExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.wordJumblesQuestions)
    ? exercise.wordJumblesQuestions.map((q) => ({
        jumbled:
          typeof q.jumbled === "string" ? q.jumbled.trim() : "",
        words: Array.isArray(q.word)
          ? q.word.map((w) => String(w).trim()).filter(Boolean)
          : [],
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {},
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setState({
      answers: {},
      hasInteracted: false,
    });

    completedRef.current = false;
  }, [exercise._id]);

  /* ================= RESET ON SHOW RESULT ================= */
  useEffect(() => {
    if (!showResults) {
      completedRef.current = false;
    }
  }, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("wordJumblesQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      [field]: value,
    };

    updateQuestions(next);
  };

  const handleWordArrayChange = (qIdx, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      words: String(value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean),
    };

    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;

    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: checked,
    };

    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;

    updateQuestions([
      ...questions,
      { jumbled: "", words: [], requireCapitalization: false },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;

    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT INPUT ================= */
  const handleInputChange = (qIdx, value) => {
    if (isEditMode || showResults) return;

    setState((prev) => {
      const answers = {
        ...prev.answers,
        [qIdx]: value,
      };

      const next = {
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      // 🔥 ALWAYS notify parent (IMPORTANT FIX)
      onComplete(answers, false);

      const allAnswered = questions.every(
        (_, i) => answers[i]?.trim()
      );

      if (allAnswered) {
        const allCorrect = questions.every((q, i) => {
          const user = normalize(
            answers[i],
            q.requireCapitalization
          );

          return q.words.some(
            (w) =>
              normalize(w, q.requireCapitalization) === user
          );
        });

        onComplete(answers, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE UI ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Jumbled Word ${qIdx + 1}`}
              value={q.jumbled}
              onChange={(v) =>
                handleQuestionChange(qIdx, "jumbled", v)
              }
              placeholder="odnyma"
              required
            />

            <StringField
              label="Correct Answer(s)"
              value={q.words.join(", ")}
              onChange={(v) =>
                handleWordArrayChange(qIdx, v)
              }
              placeholder="Monday"
              required
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                handleCapitalizationChange(qIdx, c)
              }
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= EMPTY ================= */
  if (!questions.length) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No word jumble questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  /* ================= STUDENT VIEW ================= */
  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Word Jumbles</CardTitle>
        <CardDescription>
          Unscramble each word
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const userAnswer = state.answers[qIdx] || "";

          const normalizedUser = normalize(
            userAnswer,
            q.requireCapitalization
          );

          const correctNormalized = q.words.map((w) =>
            normalize(w, q.requireCapitalization)
          );

          const isCorrect =
            userAnswer &&
            correctNormalized.includes(normalizedUser);

          return (
            <div
              key={qIdx}
              className={cn(
                "p-10 rounded-2xl text-center space-y-8 transition",

                showResults &&
                  isCorrect &&
                  "bg-green-100 border border-green-400",

                showResults &&
                  userAnswer &&
                  !isCorrect &&
                  "bg-red-100 border border-red-400",

                !showResults && "bg-muted"
              )}
            >
              <div className="text-5xl font-bold tracking-widest text-blue-700">
                {q.jumbled}
              </div>

              <Input
                value={userAnswer}
                onChange={(e) =>
                  handleInputChange(qIdx, e.target.value)
                }
                placeholder="Type the correct word"
                disabled={showResults}
                className={cn(
                  "max-w-md mx-auto text-2xl text-center h-16 font-bold",
                  showResults &&
                    isCorrect &&
                    "bg-green-100 border-green-600",
                  showResults &&
                    userAnswer &&
                    !isCorrect &&
                    "bg-red-100 border-red-600"
                )}
              />

              {showResults && userAnswer && (
                <div
                  className={cn(
                    "text-xl font-semibold",
                    isCorrect
                      ? "text-green-700"
                      : "text-red-700"
                  )}
                >
                  {isCorrect
                    ? "Correct!"
                    : `Correct: ${q.words.join(" / ")}`}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}